import React, { useState } from 'react';
import { Button, Typography, Radio, Input, Card, Row, Col, Popconfirm, Image, Space } from 'components';
import { FullscreenOutlined, DeleteOutlined } from '@ant-design/icons';
import { useRaiseClaim } from './useRaiseClaim';
import styles from './styles.module.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

const CLAIM_TYPES = [
  { value: 'MISSING_ITEM', label: 'Missing Item' },
  { value: 'WRONG_ITEM', label: 'Wrong Item' },
  { value: 'WRONG_QUANTITY', label: 'Wrong Quantity' },
  { value: 'DAMAGED_ITEM', label: 'Damaged Item' },
  { value: 'DAMAGED_PACKAGING', label: 'Damaged Packaging' },
  { value: 'OTHER', label: 'Other' },
];

export const RaiseClaim: React.FC = () => {
  const {
    isLoading,
    itemInfo,
    claimType,
    notes,
    fileList,
    isSubmitting,
    handleClaimTypeChange,
    handleNotesChange,
    handleFileSelect,
    handleRemove,
    handleSubmit,
    fileInputRef,
  } = useRaiseClaim();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const handleOpenCamera = () => {
    fileInputRef.current?.click();
  };

  const handlePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setPreviewOpen(true);
  };

  const isFormValid = claimType && (claimType === 'WRONG_QUANTITY' || claimType === 'OTHER' ? notes : true);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Title */}
        <Title level={3} className={styles.pageTitle}>
          Raise Claim
        </Title>

        {/* Item SKU */}
        {itemInfo && (
          <div className={styles.skuContainer}>
            <Text className={styles.itemSku}>
              SKU: <Text strong>{'sku' in itemInfo ? itemInfo.sku : itemInfo.itemSku}</Text>
            </Text>
          </div>
        )}

        <Card className={styles.card}>
          {/* Claim Type Section */}
          <div className={styles.section}>
            <Text className={styles.sectionLabel}>Claim Type</Text>
            <Radio.Group value={claimType} onChange={handleClaimTypeChange} style={{ width: '100%' }}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                {CLAIM_TYPES.map((type) => (
                  <Row
                    key={type.value}
                    className={styles.radioOption}
                    onClick={() => handleClaimTypeChange({ target: { value: type.value } } as any)}
                  >
                    <Col flex="auto">
                      <Text>{type.label}</Text>
                    </Col>
                    <Col>
                      <Radio value={type.value} />
                    </Col>
                  </Row>
                ))}
              </Space>
            </Radio.Group>
          </div>

          {/* Notes Section */}
          <div className={styles.section}>
            <Text className={styles.sectionLabel}>
              Notes {(claimType === 'WRONG_QUANTITY' || claimType === 'OTHER') && '*'}
            </Text>
            <TextArea
              placeholder="Provide additional details about the issue..."
              value={notes}
              onChange={handleNotesChange}
              rows={4}
              maxLength={500}
              showCount
            />
          </div>

          {/* Photo Upload Section */}
          <div className={styles.section}>
            <Text className={styles.sectionLabel}>Attach Photos (Optional)</Text>

            {/* Images Grid */}
            {fileList.length > 0 && (
              <Row gutter={[12, 12]} className={styles.imagesGrid}>
                {fileList.map((file, index) => (
                  <Col key={index} xs={8} sm={6} md={4}>
                    <div className={styles.imageCard}>
                      <img src={file.url} alt={`Photo ${index + 1}`} className={styles.gridImage} />
                      <div className={styles.imageActions}>
                        <Button
                          type="text"
                          icon={<FullscreenOutlined />}
                          size="small"
                          onClick={() => handlePreview(file.url)}
                          className={styles.imageActionBtn}
                        />
                        <Popconfirm
                          title="Delete image"
                          description="Are you sure you want to delete this image?"
                          onConfirm={() => handleRemove(file)}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            className={styles.imageActionBtn}
                          />
                        </Popconfirm>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Card>
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />

      {/* Bottom Actions */}
      <div className={styles.bottomActions}>
        <Button type="default" size="large" onClick={handleOpenCamera} block>
          Take a Photo
        </Button>

        <Button
          type="primary"
          size="large"
          danger
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          loading={isSubmitting}
          block
        >
          Raise Claim
        </Button>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
};
